import React, { useState, useEffect, useCallback } from 'react';
import API from '../../api';
import { Users, Plus, X, Edit2, Trash2, Shield, ShieldOff, UserCheck, Briefcase, HardHat, Crown, Mail, Phone, Calendar, BadgeCheck, Ghost } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';

// ──────────────────────────────────────────────────────────────────
// 3 ruoli aziendali semplificati
// ──────────────────────────────────────────────────────────────────
export const RUOLI = {
  admin: {
    label: 'Amministratore',
    icon: Crown,
    color: 'bg-red-100 text-red-700 border-red-200',
    descrizione: 'Accesso completo: gestione utenti, contabilita, tutte le funzionalita',
    permessi: ['Tutto il gestionale', 'Crea/modifica utenti', 'Cambia configurazione AI', 'Elimina dati'],
  },
  impiegato: {
    label: 'Impiegato',
    icon: Briefcase,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    descrizione: 'Ufficio tecnico/amministrativo: preventivi, fatture, clienti, pratiche',
    permessi: ['Gestisce clienti, pratiche, preventivi', 'Emette fatture e registra pagamenti', 'Usa Geppo AI', 'NO gestione utenti'],
  },
  operatore: {
    label: 'Operatore',
    icon: HardHat,
    color: 'bg-primary text-primary border-primary',
    descrizione: 'Capocantiere/operaio in cantiere: chat cantiere, foto, SAL',
    permessi: ['Chat cantiere (foto, audio, documenti)', 'Vede pratiche assegnate', 'Aggiorna SAL', 'NO contabilita'],
  },
};

const LEGACY_MAP = { ufficio: 'impiegato', tecnico: 'impiegato', capocantiere: 'operatore', collaboratore: 'operatore' };
export const normalizeRuolo = (r) => LEGACY_MAP[r] || r || 'operatore';

const EMPTY_FORM = { email: '', password: '', nome: '', cognome: '', ruolo: 'operatore', telefono: '', mansione: '', matricola: '', data_assunzione: '', costo_orario: 25 };

const isGhost = (u) => !!(u && (u.is_ghost || !u.email));


function StatRuolo({ icon: Icon, label, count, sub, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} className={color || 'text-primary'} />
        <span className="text-xs font-bold text-slate-500 uppercase">{label}</span>
      </div>
      <p className={`text-2xl font-black ${color || ''}`}>{count}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

function Row({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0">
      <Icon size={14} className="text-slate-400" />
      <span className="text-xs text-slate-500 w-24">{label}</span>
      <span className={`text-sm font-semibold flex-1 ${color || ''}`}>{value}</span>
    </div>
  );
}

function UserDetail({ user, onClose }) {
  const ruolo = normalizeRuolo(user.ruolo);
  const R = RUOLI[ruolo] || RUOLI.operatore;
  const Ric = R.icon;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto p-5 sm:p-7 shadow-2xl" data-testid="user-detail">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">Dettaglio Dipendente</h3>
          <button onClick={onClose}><X size={20} className="text-slate-400" /></button>
        </div>
        <div className="text-center mb-4">
          <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto mb-2 flex items-center justify-center text-lg font-black text-slate-600">
            {(user.nome || '?').charAt(0)}{(user.cognome || '').charAt(0)}
          </div>
          <p className="font-black text-lg">{user.nome} {user.cognome}</p>
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold border ${R.color} mt-1`}>
            <Ric size={12} /> {R.label}
          </span>
        </div>
        <div className="space-y-2 text-sm">
          <Row icon={isGhost(user) ? Ghost : Mail} label="Email" value={isGhost(user) ? 'Operatore senza login' : user.email} color={isGhost(user) ? 'text-slate-500 italic' : ''} />
          <Row icon={Phone} label="Telefono" value={user.telefono || '-'} />
          <Row icon={Briefcase} label="Mansione" value={user.mansione || '-'} />
          <Row icon={BadgeCheck} label="Matricola" value={user.matricola || '-'} />
          <Row icon={Calendar} label="Assunto il" value={user.data_assunzione ? new Date(user.data_assunzione).toLocaleDateString('it-IT') : '-'} />
          <Row icon={UserCheck} label="Stato" value={user.attivo !== false ? 'Attivo' : 'Disattivato'} color={user.attivo !== false ? 'text-green-600' : 'text-red-600'} />
        </div>
        <div className={`mt-4 p-3 rounded border ${R.color}`}>
          <p className="text-xs font-bold uppercase mb-2">Permessi</p>
          <ul className="text-xs space-y-0.5">
            {R.permessi.map((p) => <li key={p}>· {p}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

function UserFormModal({ form, setForm, editing, onClose, onSave }) {
  const ghost = !form.email.trim();
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-4xl max-h-[92vh] overflow-y-auto p-5 sm:p-7 shadow-2xl" data-testid="user-form-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">{editing ? 'Modifica Dipendente' : 'Nuovo Dipendente'}</h3>
          <button onClick={onClose} data-testid="close-form"><X size={20} className="text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="user-nome" placeholder="Nome *" className="input w-full" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
            <input data-testid="user-cognome" placeholder="Cognome *" className="input w-full" value={form.cognome} onChange={e => setForm({ ...form, cognome: e.target.value })} />
          </div>
          <div>
            <input data-testid="user-email" type="email" placeholder="Email (opzionale per operatori senza login)" className="input w-full" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            {ghost && (
              <div className="mt-1 flex items-start gap-1.5 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                <Ghost size={12} className="mt-0.5 text-slate-400 shrink-0" />
                <span><b>Operatore senza login</b>: viene censito solo per timesheet, costi orari e SAL. Non potra accedere al gestionale.</span>
              </div>
            )}
          </div>
          {!ghost && (
            <input data-testid="user-password" type="password" placeholder={editing ? "Nuova password (lascia vuoto per non cambiare)" : "Password *"} className="input w-full" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
          )}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Ruolo aziendale</label>
            <div className="grid grid-cols-3 gap-2" data-testid="ruolo-selector">
              {Object.entries(RUOLI).map(([k, r]) => {
                const Ric = r.icon;
                const active = form.ruolo === k;
                return (
                  <button key={k} type="button" onClick={() => setForm({ ...form, ruolo: k })} data-testid={`ruolo-${k}`}
                    className={`p-2 rounded-md border-2 text-xs transition-all ${active ? r.color + ' border-current font-bold' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}>
                    <Ric size={16} className="mx-auto mb-1" />
                    <span>{r.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-1">{RUOLI[form.ruolo]?.descrizione}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Telefono" className="input w-full" value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} />
            <input placeholder="Matricola" className="input w-full" value={form.matricola} onChange={e => setForm({ ...form, matricola: e.target.value })} />
          </div>
          <input placeholder="Mansione (es. Capocantiere, Muratore, Responsabile ufficio)" className="input w-full" value={form.mansione} onChange={e => setForm({ ...form, mansione: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Data assunzione</label>
              <input type="date" className="input w-full" value={form.data_assunzione} onChange={e => setForm({ ...form, data_assunzione: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Costo orario (EUR/h)</label>
              <input type="number" step="0.5" min="0" className="input w-full" value={form.costo_orario} onChange={e => setForm({ ...form, costo_orario: e.target.value })} />
            </div>
          </div>
          <button data-testid="save-user-btn" onClick={onSave} className="w-full py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary/90 transition-all">
            {editing ? 'Salva Modifiche' : 'Crea Dipendente'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UsersTable({ utenti, onView, onEdit, onToggle, onDelete }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500">Dipendente</th>
              <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500 hidden md:table-cell">Email</th>
              <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500">Ruolo</th>
              <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500 hidden lg:table-cell">Mansione</th>
              <th className="text-center px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500">Stato</th>
              <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider text-slate-500">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {utenti.length === 0 && <tr><td colSpan="6" className="text-center text-slate-400 py-8">Nessun dipendente. Crea il primo account.</td></tr>}
            {utenti.map(u => {
              const ruolo = normalizeRuolo(u.ruolo);
              const R = RUOLI[ruolo] || RUOLI.operatore;
              const Ric = R.icon;
              return (
                <tr key={u.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${u.attivo === false ? 'opacity-50' : ''}`} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-xs font-bold text-slate-600">
                        {(u.nome || '?').charAt(0)}{(u.cognome || '').charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold">{u.nome} {u.cognome}</p>
                        {u.matricola && <p className="text-[10px] text-slate-400 font-mono">#{u.matricola}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm hidden md:table-cell">
                    {isGhost(u) ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                        <Ghost size={10} /> Senza login
                      </span>
                    ) : (
                      <p>{u.email}</p>
                    )}
                    {u.telefono && <p className="text-xs text-slate-400">{u.telefono}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold border ${R.color}`}>
                      <Ric size={12} /> {R.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 hidden lg:table-cell">{u.mansione || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${u.attivo !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {u.attivo !== false ? 'Attivo' : 'Disattivato'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => onView(u)} className="p-1.5 hover:bg-slate-100 rounded" title="Dettagli" data-testid={`detail-user-${u.id}`}>
                        <BadgeCheck size={14} className="text-slate-500" />
                      </button>
                      <button onClick={() => onEdit(u)} className="p-1.5 hover:bg-slate-100 rounded" title="Modifica" data-testid={`edit-user-${u.id}`}>
                        <Edit2 size={14} className="text-slate-400" />
                      </button>
                      <button onClick={() => onToggle(u.id)} className="p-1.5 hover:bg-slate-100 rounded" title={u.attivo !== false ? 'Disattiva' : 'Attiva'} data-testid={`toggle-user-${u.id}`}>
                        {u.attivo !== false ? <ShieldOff size={14} className="text-yellow-500" /> : <Shield size={14} className="text-green-500" />}
                      </button>
                      {ruolo !== 'admin' && (
                        <button onClick={() => onDelete(u.id)} className="p-1.5 hover:bg-red-50 rounded" title="Elimina" data-testid={`delete-user-${u.id}`}>
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default function UserManagementTab() {
  const confirm = useConfirm();
  const [utenti, setUtenti] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(() => API.get('/api/utenti').then(r => setUtenti(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.nome || !form.cognome) { alert('Nome e cognome obbligatori'); return; }
    const ghost = !form.email.trim();
    if (!ghost && !editing && !form.password) { alert('Password obbligatoria per nuovi utenti con email'); return; }
    try {
      const payload = { ...form };
      if (ghost) { payload.email = ''; delete payload.password; }
      else if (!payload.password) delete payload.password;
      if (editing) await API.put(`/api/utenti/${editing}`, payload);
      else await API.post('/api/utenti', payload);
      setShowForm(false); setEditing(null); setForm(EMPTY_FORM);
      load();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
  };

  const openEdit = (u) => {
    setForm({
      email: u.email, password: '', nome: u.nome || '', cognome: u.cognome || '',
      ruolo: normalizeRuolo(u.ruolo), telefono: u.telefono || '',
      mansione: u.mansione || '', matricola: u.matricola || '',
      data_assunzione: u.data_assunzione ? u.data_assunzione.slice(0, 10) : '',
      costo_orario: u.costo_orario || 25,
    });
    setEditing(u.id);
    setShowForm(true);
  };

  const deleteUser = async (id) => {
    if (!await confirm({ title: 'Eliminare dipendente?', message: 'L\'account verrà rimosso definitivamente. Operazione irreversibile.', confirmLabel: 'Elimina dipendente', variant: 'danger' })) return;
    try { await API.delete(`/api/utenti/${id}`); load(); }
    catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
  };

  const toggleUser = async (id) => {
    try { await API.put(`/api/utenti/${id}/toggle`); load(); }
    catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
  };

  const attivi = utenti.filter(u => u.attivo !== false).length;
  const admins = utenti.filter(u => normalizeRuolo(u.ruolo) === 'admin').length;
  const impiegati = utenti.filter(u => normalizeRuolo(u.ruolo) === 'impiegato').length;
  const operatori = utenti.filter(u => normalizeRuolo(u.ruolo) === 'operatore').length;

  return (
    <div data-testid="user-management-tab">
      <div className="flex justify-end mb-4">
        <button data-testid="add-user-btn" onClick={() => { setShowForm(true); setEditing(null); setForm(EMPTY_FORM); }}
          className="px-4 py-2 bg-primary text-white font-bold rounded-md hover:bg-primary/90 transition-all flex items-center gap-2 text-sm">
          <Plus size={16} /> Nuovo Dipendente
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatRuolo icon={Users} label="Totale" count={utenti.length} sub={`${attivi} attivi`} />
        <StatRuolo icon={Crown} label="Amministratori" count={admins} color="text-red-600" />
        <StatRuolo icon={Briefcase} label="Impiegati" count={impiegati} color="text-blue-600" />
        <StatRuolo icon={HardHat} label="Operatori" count={operatori} color="text-primary" />
      </div>

      <UsersTable
        utenti={utenti}
        onView={setDetail}
        onEdit={openEdit}
        onToggle={toggleUser}
        onDelete={deleteUser}
      />

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        {Object.entries(RUOLI).map(([k, r]) => {
          const Ric = r.icon;
          return (
            <div key={k} className={`rounded-md p-4 border ${r.color}`}>
              <div className="flex items-center gap-2 mb-2">
                <Ric size={18} />
                <h3 className="font-black">{r.label}</h3>
              </div>
              <p className="text-xs mb-2 opacity-80">{r.descrizione}</p>
              <ul className="text-xs space-y-0.5 opacity-90">
                {r.permessi.map((p) => <li key={p}>· {p}</li>)}
              </ul>
            </div>
          );
        })}
      </div>

      {showForm && <UserFormModal form={form} setForm={setForm} editing={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSave={save} />}
      {detail && <UserDetail user={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
